import * as echarts from "echarts";

let charts = [];

function getZoomConfig(chartData) {
    let zoomThreshold =
        window.innerWidth < 768
            ? chartData.axisData.mobileZoomThreshold ?? 10
            : chartData.axisData.zoomThreshold ?? 20;

    return chartData.labels.length > zoomThreshold
        ? [
              {
                  type: "slider",
                  orient: "horizontal",
                  xAxisIndex: 0,
                  start: 0,
                  end: 40,
                  showDetail: false, //remove labels from datazoom
              },
          ]
        : []; // No zoom if the threshold isn't met
}

function convertTimeToMinutes(timeString) {
    if (typeof timeString !== "string") {
        return timeString; // If it's already a number, return it as is
    }

    let parts = timeString.split(":").map(Number);
    let hours = parts[0] || 0;
    let minutes = parts[1] || 0;
    let seconds = parts[2] || 0;
    let totalMinutes = hours * 60 + minutes + seconds / 60;

    return parseFloat(totalMinutes.toFixed(2));
}

function preprocessChartData(chartData) {
    chartData.datasets.forEach((dataset) => {
        if (dataset.dataType === "time") {
            dataset.data = dataset.data.map(convertTimeToMinutes);
        }
    });
    return chartData;
}

function formatMinutesToTime(value) {
    let hours = Math.floor(value / 60);
    let minutes = Math.floor(value % 60);
    let seconds = Math.round((value - Math.floor(value)) * 60);

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
        2,
        "0"
    )}:${String(seconds).padStart(2, "0")}`;
}

function initializeCharts() {
    let chartContainers = document.querySelectorAll(".eChartHorizontal");

    chartContainers.forEach((chartContainer, index) => {
        let chartDataJson = chartContainer.getAttribute("data-chart");

        if (!chartDataJson) {
            console.error("Missing data-chart attribute on:", chartContainer);
            return;
        }

        let chartData;
        try {
            chartData = JSON.parse(chartDataJson);
        } catch (error) {
            console.error("Error parsing JSON data:", error);
            return;
        }

        chartData = preprocessChartData(chartData);

        let myChart =
            charts[index] ||
            echarts.init(chartContainer, null, { renderer: "svg" });

        let option = {
            grid: {
                bottom: chartData.axisData.chartBottomPadding ?? 120,
                left: chartData.axisData.chartLeftPadding ?? 0,
                containLabel: true,
            },
            nameTextStyle: {
                color: "#376272",
                fontFamily: "Inter, Arial, sans-serif",
                fontSize: 16,
            },
            textStyle: {
                color: "#000000",
                fontFamily: "Inter, Arial, sans-serif",
                fontSize: 16,
            },
            tooltip: {
                trigger: "axis",
                axisPointer: {
                    type: "shadow",
                },
            },
            legend: false,
            xAxis: {
                name: chartData.axisData.xTitle,
                nameGap: chartData.axisData.xTitleGap ?? 40,
                nameLocation: "center",
                position: "center",
                type: "value",
                axisLabel: {
                    color: "#000000",
                    fontFamily: "Inter, Arial, sans-serif",
                    fontSize: 16,
                    formatter: function (value) {
                        let dataset = chartData.datasets.find(
                            (ds) => ds.yAxisID === "y-axis-horizontal"
                        );
                        if (dataset?.dataType === "time") {
                            return formatMinutesToTime(value);
                        }
                        return `${value} ${dataset?.units ?? ""}`;
                    },
                    rotate:
                        window.innerWidth < 768
                            ? 45
                            : chartData.axisData.xRotation ?? 0,
                },
                axisLine: {
                    show: false,
                },
                axisTick: {
                    alignWithLabel: true,
                },
            },
            yAxis: [
                {
                    type: "category",
                    data: chartData.labels,
                    name: chartData.axisData.yTitleLeft,
                    position: "left",
                    nameGap: 80,
                    nameLocation: "center",
                    axisLabel: {
                        color: "#000000",
                        fontFamily: "Inter, Arial, sans-serif",
                        fontSize: 16,
                        formatter: function (value) {
                            let dataset = chartData.datasets.find(
                                (ds) => ds.yAxisID === "y-axis-left"
                            );
                            if (dataset?.dataType === "time") {
                                return formatMinutesToTime(value);
                            }
                            return `${value} ${dataset?.units ?? ""}`;
                        },
                    },
                    axisLine: {
                        show: false,
                    },
                    axisTick: {
                        show: false,
                    },
                },
            ],
            series: chartData.datasets.map((ds) => {
                let baseConfig = {
                    name: ds.label,
                    type: ds.type,
                    data: ds.data,
                    emphasis: {
                        focus: "series",
                    },
                    stack: ds.stack,
                    itemStyle: {
                        color: ds.backgroundColor,
                        borderColor: ds.borderColor,
                        borderRadius: [0, 0, 0, 0],
                        borderWidth: ds.borderWidth,
                    },
                    interval: ds.interval ?? "",
                    label: {
                        show: false,
                    },
                };

                if (ds.type === "bar") {
                    Object.assign(baseConfig, {
                        barWidth: ds.barWidth ?? "80%",
                        barGap: ds.barGap ?? "5%",
                        label: {
                            ...baseConfig.label,
                        },
                    });

                    if (ds.stackOrder === "last") {
                        Object.assign(baseConfig, {
                            itemStyle: {
                                color: ds.backgroundColor,
                                borderColor: ds.borderColor,
                                borderWidth: ds.borderWidth,
                                borderRadius: [0, 4, 4, 0],
                            },
                        });
                    }

                    if (ds.stackOrder === "first") {
                        Object.assign(baseConfig, {
                            itemStyle: {
                                color: ds.backgroundColor,
                                borderColor: ds.borderColor,
                                borderWidth: ds.borderWidth,
                                borderRadius: [4, 0, 0, 4],
                            },
                        });
                    }
                }

                return baseConfig;
            }),
            tooltip: {
                trigger: "axis",
                axisPointer: {
                    type: "shadow",
                },
                formatter: function (params) {
                    let tooltipText = params[0].axisValueLabel + "<br/>";
                    params.forEach((param) => {
                        let dataset = chartData.datasets.find(
                            (ds) => ds.label === param.seriesName
                        );
                        let isTimeBased = dataset?.dataType === "time";
                        let formattedValue = isTimeBased
                            ? formatMinutesToTime(param.value)
                            : param.value;
                        tooltipText += `${param.seriesName}: ${formattedValue}<br/>`;
                    });
                    return tooltipText;
                },
            },
            dataZoom: getZoomConfig(chartData),
        };

        try {
            myChart.setOption(option);
        } catch (error) {
            console.error("Error setting chart option:", error);
        }

        // Update charts array to keep track of initialized charts
        charts[index] = myChart;
    });
}

// Ensure script runs after the DOM has loaded
document.addEventListener("DOMContentLoaded", initializeCharts);

// Resize existing charts instead of reinitializing
window.addEventListener("resize", () => {
    charts.forEach((chart) => {
        let chartData = JSON.parse(chart.getDom().dataset.chart);
        chart.setOption({
            dataZoom: getZoomConfig(chartData),
        });

        chart.resize();
    });
});
